import { NavLink, useLocation } from 'react-router-dom';
import { UserGroupPermissions } from './UserGroupPermissions';
import { useAppContext } from '../../providers/AppProvider';
import VisualScroll from './VisualScroll';
import { useIntl } from 'react-intl';
import { useEffect, useState } from 'react';

const SideMenu = ({ menuItems, isOpen, logout, setNavState, mobileBreakpoint }) => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const subNavItemHeight = 40; // must match height of .sub-nav > li > a in '_side-nav.scss'
  const disableSideNav = pathname.split('/')[1] === 'site-selection';
  const { theme } = useAppContext();

  const isActiveDescendent = (to) => {
    const toSegments = String(to).split('/').filter(Boolean);
    const pathSegments = pathname.split('/').filter(Boolean);
    return toSegments.every((seg, i) => pathSegments[i] === seg);
  };

  const [expandedItems, setExpandedItems] = useState(() => {
    const initial = new Set();
    menuItems.forEach((item) => {
      if (item.subItems?.length && isActiveDescendent(item.to)) {
        initial.add(item.to);
      }
    });
    return initial;
  });

  // Auto-expand parent when navigating to a child route
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.subItems?.length && isActiveDescendent(item.to)) {
        setExpandedItems((prev) => {
          if (prev.has(item.to)) return prev;
          const next = new Set(prev);
          next.add(item.to);
          return next;
        });
      }
    });
  }, [pathname]);

  const toggleSubNav = (to) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(to)) next.delete(to);
      else next.add(to);
      return next;
    });
  };

  const closeMenu = () => {
    if (window.innerWidth <= mobileBreakpoint) setNavState(false);
  };

  const renderMenuItem = (index, root = false) => {
    const hasSubItems = Boolean(index.subItems?.length);
    const isExpanded = expandedItems.has(index.to);

    const isExactActive = () => pathname === index.to;

    return (
      <UserGroupPermissions key={index.to} itemRef={index.permissionRef}>
        <li className={index.className}>
          {hasSubItems ? (
            <button
              className={`nav-parent${isActiveDescendent(index.to) ? ' active' : ''}`}
              onClick={() => toggleSubNav(index.to)}
              title={intl.formatMessage({ id: index.label })}
            >
              {index.icon && <span className={`uil ${index.icon}`} />}
              <span className={`label ${isOpen ? 'pl-2' : ''}`}>
                {intl.formatMessage({ id: index.label })}
              </span>
              <span
                className={`uil uil-angle-down nav-chevron${
                  isExpanded ? ' nav-chevron--open' : ''
                }`}
              />
            </button>
          ) : (
            <NavLink
              to={index.to}
              activeClassName="active"
              isActive={root ? () => isActiveDescendent(index.to) : isExactActive}
              onClick={closeMenu}
              title={intl.formatMessage({ id: index.label })}
            >
              {index.icon && <span className={`uil ${index.icon}`} />}
              <span className={`label ${isOpen ? 'pl-2' : ''}`}>
                {intl.formatMessage({ id: index.label })}
              </span>
            </NavLink>
          )}
          {hasSubItems && (
            <ul
              className={`sub-nav${isExpanded ? ' expanded' : ''}`}
              style={{ '--expandedHeight': `${subNavItemHeight * index.subItems.length}px` }}
            >
              {index.subItems.map((j) => renderMenuItem(j))}
            </ul>
          )}
        </li>
      </UserGroupPermissions>
    );
  };

  return (
    <nav className={`main ${isOpen ? 'expanded' : ''}`}>
      <div className="top">
        <img src={theme.logo} alt="logo" className="logo" />
        <button onClick={() => setNavState(!isOpen)} className="nav-toggle">
          <i className="uil uil-bars"></i>
          <i className="uil uil-angle-left"></i>
        </button>
      </div>
      {!disableSideNav && (
        <VisualScroll classes="middle">
          <ul>{menuItems.map((i) => renderMenuItem(i, true))}</ul>
        </VisualScroll>
      )}
      <div className="bottom">
        <div className="responsive-link">
          <button className="username" onClick={logout}>
            <span
              className={`uil uil-left-arrow-from-left ${isOpen || disableSideNav ? 'pr-2' : ''}`}
            />
            <span>{isOpen || disableSideNav ? intl.formatMessage({ id: 'signOut' }) : ''}</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default SideMenu;
